import { requestPresignedDownload } from '../api/files';
import { registerSessionCache } from './session-caches';

/**
 * presigned 다운로드 URL 캐시 (chat-media-date-groups — 2026-08-07).
 *
 * 우측 미디어 패널의 이미지 썸네일은 지금까지 **썸네일 1개당 presign POST 1회**를 무캐시로 날렸다.
 * 일자 아코디언을 도입하면 접기→펼치기·탭 전환·라이트박스 열기에서 같은 파일을 반복 요청하게 되므로
 * 얇은 캐시가 필요하다.
 *
 * - **in-flight 중복 제거**: 같은 fileId 에 대한 동시 요청은 하나의 Promise 를 공유한다.
 * - **TTL 60초**: 서버 presign 만료(5분)보다 보수적으로 잡아, 만료 직전 URL 을 물려주는 사고를 피한다.
 * - 실패한 요청은 캐시에 남기지 않는다(다음 시도에서 재요청).
 *
 * additive — 기존 `MediaImageThumb`/`AttachmentList` 등은 계속 `requestPresignedDownload` 를 직접 쓴다.
 * 이 캐시는 신규 미디어 브라우저 경로에서만 사용한다(회귀 0).
 */

/** 캐시 수명(ms). 서버 만료 5분보다 짧게 — 만료 임박 URL 재사용 방지. */
export const PRESIGN_CACHE_TTL_MS = 60_000;

interface Entry {
  promise: Promise<string>;
  at: number;
}

const cache = new Map<string, Entry>();

/**
 * presigned GET URL 조회(캐시 경유).
 *
 * @param fileId 첨부 fileId (숫자 문자열)
 * @param nowMs  현재 시각 주입(테스트용). 미지정 시 실제 시각.
 * @param purpose 감사 이름표(계약 §3.4.1) — **`'preview'` 만** 받는다. 이 캐시를 쓰는 곳(썸네일 · 라이트박스 보기 · PDF 미리보기)은 전부 화면에 보이기만 한다.
 *
 * ★ 2026-09-14(#1268 리뷰 메모) — 키에 목적을 넣는다. 내려받기(`download`)는 **이 캐시를 타지 않는다** — 썸네일이 preview 로 받아 둔
 *   주소를 「내려받기」 가 재사용하면 «내려받음» 감사가 빠진다. 내려받기 단추는 매번 `requestPresignedDownload` 를 직접 부른다.
 */
export function getPresignedUrl(fileId: string, nowMs?: number, purpose: 'preview' = 'preview'): Promise<string> {
  const now = nowMs ?? Date.now();
  const key = `${purpose}:${fileId}`;
  const hit = cache.get(key);
  if (hit && now - hit.at < PRESIGN_CACHE_TTL_MS) return hit.promise;

  const promise = requestPresignedDownload(fileId, { purpose })
    .then((res) => res.downloadUrl)
    .catch((err) => {
      // 실패는 캐시에 남기지 않는다 — 다음 호출이 다시 시도할 수 있어야 한다.
      if (cache.get(key)?.promise === promise) cache.delete(key);
      throw err;
    });

  cache.set(key, { promise, at: now });
  return promise;
}

/** 테스트·방 전환용 전체 무효화. */
export function clearPresignedUrlCache(): void {
  cache.clear();
}

// ★★ 로그아웃하면 비운다 (2026-08-31) — **앞 사람이 열 수 있던 파일의 서명 주소**다.
//    서명은 잠깐 살아 있으므로, 남겨 두면 다음 사람 화면에서 그대로 열린다.
registerSessionCache(clearPresignedUrlCache);
